import React, { useMemo } from 'react';

/**
 * A generated avatar for players who never upload a picture — which is everyone,
 * since guests have no profile.
 *
 * The image is derived from the name, not randomised per render: the same guest
 * keeps the same face for the whole game, and everyone at the table sees the same
 * one for them. Bots get a robot instead so they stay obvious at a glance.
 */

// FNV-1a: tiny, stable, and well spread for short strings.
const hashOf = (value) => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
};

const isBotName = (name) => typeof name === 'string' && name.includes('#BOT#');

const PlayerAvatar = ({ name, size = 40, className = '', title = '' }) => {
  const art = useMemo(() => {
    const seed = hashOf(name || '?');

    // Two hues a fixed distance apart always give a readable gradient.
    const hue = seed % 360;
    const hue2 = (hue + 42) % 360;
    const from = `hsl(${hue} 68% 52%)`;
    const to = `hsl(${hue2} 72% 42%)`;
    const ink = `hsl(${hue} 90% 96%)`;

    // Classic identicon: fill a 3x5 grid and mirror it, so the result reads as a
    // deliberate symbol rather than noise.
    const cells = [];
    for (let x = 0; x < 3; x++) {
      for (let y = 0; y < 5; y++) {
        if ((seed >> (x * 5 + y)) & 1) {
          cells.push([x, y]);
          if (x < 2) cells.push([4 - x, y]);
        }
      }
    }
    return { from, to, ink, cells, seed };
  }, [name]);

  const bot = isBotName(name);
  const gradientId = `av-${art.seed.toString(36)}`;
  const label = title || (bot ? 'Bot' : name || 'Player');

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      className={className}
      role="img"
      aria-label={`${label} avatar`}
      style={{ display: 'block', borderRadius: '50%' }}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={art.from} />
          <stop offset="100%" stopColor={art.to} />
        </linearGradient>
      </defs>
      <rect width="40" height="40" rx="20" fill={`url(#${gradientId})`} />
      {bot ? (
        <text x="20" y="27" textAnchor="middle" fontSize="20">🤖</text>
      ) : (
        <g fill={art.ink} opacity="0.92">
          {art.cells.map(([x, y]) => (
            <rect key={`${x}-${y}`} x={5 + x * 6} y={5 + y * 6} width="6" height="6" rx="1.4" />
          ))}
        </g>
      )}
    </svg>
  );
};

export default PlayerAvatar;
